import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { SERVICES, PACKAGES } from "../../data/staticData";
import { SERVICE_FORMS } from "../../data/serviceForms";
import { api } from "../../lib/api";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";

export default function NewOrder() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialService = params.get("service") || SERVICES[0].slug;
  const initialPackage = params.get("package");

  const [serviceSlug, setServiceSlug] = useState(initialService);
  const [packageId, setPackageId] = useState(initialPackage || PACKAGES[initialService]?.[0]?.id);
  const [requirements, setRequirements] = useState({});
  const [loading, setLoading] = useState(false);

  const packages = useMemo(() => PACKAGES[serviceSlug] || [], [serviceSlug]);
  const selectedPkg = useMemo(() => packages.find((p) => p.id === packageId), [packages, packageId]);
  const service = SERVICES.find((s) => s.slug === serviceSlug);
  const form = SERVICE_FORMS[serviceSlug];

  // Reset requirements when service changes
  useEffect(() => {
    setRequirements({});
  }, [serviceSlug]);

  const handleService = (slug) => {
    setServiceSlug(slug);
    setPackageId(PACKAGES[slug]?.[0]?.id);
  };

  const setField = (key, value) => setRequirements((r) => ({ ...r, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    // basic required validation
    const missing = (form?.fields || []).filter((f) => f.required && !requirements[f.key]);
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        service_slug: serviceSlug,
        package_id: packageId,
        package_name: `${service.title} · ${selectedPkg?.name}`,
        price: selectedPkg?.price,
        requirements,
      });
      toast.success("Order submitted. Our team will pick it up shortly.");
      nav(`/dashboard/orders/${data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="eyebrow mb-2">Place an order</div>
      <h1 className="font-display text-4xl font-bold tracking-tight">New campaign</h1>

      <form onSubmit={submit} className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="new-order-form">
        <div className="lg:col-span-2 space-y-5">
          {/* Step 1: Service */}
          <div className="card-soft p-6">
            <div className="eyebrow mb-3">1 · Choose service</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {SERVICES.map((s) => {
                const Icon = s.icon;
                const active = s.slug === serviceSlug;
                return (
                  <button
                    type="button"
                    key={s.slug}
                    onClick={() => handleService(s.slug)}
                    className={`p-3 text-left rounded-xl border transition ${active ? "border-ink bg-ink text-white" : "border-line hover:border-ink"}`}
                    data-testid={`new-order-service-${s.slug}`}
                  >
                    <Icon size={16} className={active ? "text-white" : "text-terra"} />
                    <div className="mt-2 text-xs font-medium leading-tight">{s.title}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Package */}
          <div className="card-soft p-6">
            <div className="eyebrow mb-3">2 · Pick a package</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {packages.map((p) => {
                const active = p.id === packageId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPackageId(p.id)}
                    className={`text-left rounded-xl border p-4 transition ${active ? "border-terra ring-2 ring-terra bg-terra/5" : "border-line hover:border-ink"}`}
                    data-testid={`new-order-package-${p.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-display text-base font-semibold tracking-tight">{p.name}</div>
                      {active && <Check size={16} className="text-terra" />}
                    </div>
                    <div className="font-mono text-2xl font-semibold tracking-tighter mt-1">${p.price}</div>
                    <div className="text-xs text-inkSoft mt-1">{p.turnaround}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 3: Dynamic per-service brief */}
          <div className="card-soft p-6">
            <div className="eyebrow mb-3">3 · {form?.title || "Tell us about the campaign"}</div>
            {!form ? (
              <p className="text-sm text-inkSoft">No brief required for this service.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {form.fields.map((f) => (
                  <DynamicField
                    key={f.key}
                    field={f}
                    value={requirements[f.key] ?? ""}
                    onChange={(v) => setField(f.key, v)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Summary sidebar */}
        <aside className="card-soft p-6 h-max lg:sticky lg:top-6">
          <div className="eyebrow mb-3">Order summary</div>
          <Row k="Service" v={service?.title} />
          <Row k="Package" v={selectedPkg?.name} />
          <Row k="Turnaround" v={selectedPkg?.turnaround} />
          <Row k="Total" v={selectedPkg?.price ? `$${selectedPkg.price}` : "—"} highlight />
          <button type="submit" disabled={loading} className="btn-primary mt-6 w-full justify-center text-sm" data-testid="new-order-submit">
            {loading ? "Placing…" : (<>Place order <ArrowUpRight size={16} /></>)}
          </button>
          <p className="text-[11px] text-inkSoft mt-3 text-center">Your brief routes straight to the Zenith Ranking fulfillment team. You'll see live progress in your dashboard.</p>
        </aside>
      </form>
    </DashboardLayout>
  );
}

function DynamicField({ field, value, onChange }) {
  const common = "mt-2 w-full px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink";
  const inputCls = `${common} h-11`;
  const required = field.required;
  const span = field.full ? "sm:col-span-2" : "";
  if (field.type === "textarea") {
    return (
      <label className={`block ${span}`}>
        <span className="text-xs uppercase tracking-widest text-inkSoft">{field.label}{required && " *"}</span>
        <textarea
          rows={4}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${common} py-3`}
          placeholder={field.placeholder}
          maxLength={field.max}
          data-testid={`req-${field.key}`}
        />
        {field.helper && <span className="text-[11px] text-inkSoft mt-1">{field.helper}</span>}
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <label className={`block ${span}`}>
        <span className="text-xs uppercase tracking-widest text-inkSoft">{field.label}{required && " *"}</span>
        <select
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          data-testid={`req-${field.key}`}
        >
          <option value="">Select…</option>
          {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label className={`block ${span}`}>
      <span className="text-xs uppercase tracking-widest text-inkSoft">{field.label}{required && " *"}</span>
      <input
        type={field.type || "text"}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
        placeholder={field.placeholder}
        data-testid={`req-${field.key}`}
      />
    </label>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className={`flex items-center justify-between py-2 border-b border-line last:border-0 text-sm ${highlight ? "pt-4 border-b-0" : ""}`}>
      <span className="text-inkSoft">{k}</span>
      <span className={`font-medium ${highlight ? "font-mono text-xl text-ink" : "font-mono text-xs"}`}>{v}</span>
    </div>
  );
}
